import { injectable } from 'inversify';

import { Authority, User } from '@project44/ui-utils';

import { UserCreationFormTemplate } from './templates/UserCreationFormTemplate';
import { AuthAdminUserCreatorTemplate } from './templates/userCreationFormTemplate/AuthAdminUserCreatorTemplate';
import { P44AdminUserCreatorTemplate } from './templates/userCreationFormTemplate/P44AdminUserCreatorTemplate';
import { TenantAdminUserCreatorTemplate } from './templates/userCreationFormTemplate/TenantAdminUserCreatorTemplate';

export enum UserModelTemplateResolverStrategy {
  CREATE_USER = 'CREATE_USER',
}

enum UserPrincipalActorEnum {
  SYSTEM_ADMIN = 'SYSTEM_ADMIN',
  AUTH_ADMIN = 'AUTH_ADMIN',
  TENANT_ADMIN = 'TENANT_ADMIN',
}

@injectable()
export class UserFormResolver {
  public readonly resolveUserCreationFormTemplate = (payload: {
    userPrincipal: User;
  }): UserCreationFormTemplate => {
    const { userPrincipal } = payload;

    const resolvedTemplate = this.identifyUserPrincipal(userPrincipal);

    if (resolvedTemplate === undefined) {
      throw new Error(
        `undefined IUserModelTemplate with user ${userPrincipal.id}`,
      );
    }

    return this.resolverConfigMap[
      UserModelTemplateResolverStrategy.CREATE_USER
    ][resolvedTemplate];
  };

  private readonly identifyUserPrincipal = (
    userPrincipal: User,
  ): UserPrincipalActorEnum | undefined => {
    switch (true) {
      case userPrincipal.hasAnyOfPermissions([
        <Authority>'AUTHORIZATION_ADMIN',
      ]):
        return UserPrincipalActorEnum.AUTH_ADMIN;
      case userPrincipal.hasAnyOfPermissions([
        <Authority>'system-admin:create:users',
      ]):
        return UserPrincipalActorEnum.SYSTEM_ADMIN;
      case userPrincipal.hasAnyOfPermissions([<Authority>'SAVE_USER_ROLES']):
        return UserPrincipalActorEnum.TENANT_ADMIN;
      default:
        return undefined;
    }
  };

  private get resolverConfigMap(): {
    [UserModelTemplateResolverStrategy.CREATE_USER]: {
      [K in UserPrincipalActorEnum]: UserCreationFormTemplate;
    };
  } {
    const { AUTH_ADMIN, SYSTEM_ADMIN, TENANT_ADMIN } = UserPrincipalActorEnum;
    const { CREATE_USER } = UserModelTemplateResolverStrategy;

    return {
      [CREATE_USER]: {
        [AUTH_ADMIN]: AuthAdminUserCreatorTemplate.create(),
        [SYSTEM_ADMIN]: P44AdminUserCreatorTemplate.create(),
        [TENANT_ADMIN]: TenantAdminUserCreatorTemplate.create(),
      },
    };
  }
}
